import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  useGetArtefactScope,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { ArtefactType, SaveArtefactFunction } from '@modules/common/types';
import { getArtefactTypeLabel } from '@modules/common/utils';
import { FeedDefinitionInfo } from '@modules/feed/components/FeedDefinitionInfo';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { Scope as EScope } from 'generated/graphql';
import { ChangeEvent, FormEvent, useState } from 'react';
import { Scope } from 'v2/core/entities/scope';
import { EscHotKeyIcon, ReturnHotKeyIcon } from '../HotKeyIcons';

interface SaveArtefactProps {
  onSubmit: SaveArtefactFunction | (() => Promise<void>);
  artefactType: ArtefactType;
  onCopyLink?: (link: string) => void;
  onClose?: () => void;
  headerText?: string;
  buttonText?: string;
  onProceedToSubscribe?: () => void;
  defaultValue?: string;
  shouldShowArtefactDefinitionInfo?: boolean;
  variant?: 'create' | 'update';
  shouldIncludeDashboardFilters?: boolean;
  secondaryActionNode?: React.ReactNode;
}

export const SaveArtefact = ({
  onSubmit,
  artefactType,
  onCopyLink,
  onClose,
  headerText,
  buttonText,
  onProceedToSubscribe,
  defaultValue,
  shouldShowArtefactDefinitionInfo = true,
  variant = 'create',
  shouldIncludeDashboardFilters,
  secondaryActionNode,
}: SaveArtefactProps) => {
  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const [name, setName] = useState(defaultValue ?? '');
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccessNotification } = useNotificationContext();

  const { closeModal } = useMuiModalContext();

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
      return;
    }
    closeModal();
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setName(value);
  };

  const artefactScope = useGetArtefactScope();

  const artefactTypeLabel = getArtefactTypeLabel(
    artefactType,
    isSearchToFeedEnabled,
  );

  const isSaveDisabled = !name.trim() && variant === 'create';

  const onArtefactSave = async (e: FormEvent) => {
    e.preventDefault();

    const scope =
      artefactScope === EScope.Private ? Scope.PRIVATE : Scope.PUBLIC;

    if (isSaveDisabled) {
      return;
    }
    setIsLoading(true);
    const resp = await onSubmit(name, scope);

    if (onCopyLink && resp && resp.artefactLink) {
      navigator.clipboard.writeText(resp.artefactLink);
      onCopyLink(resp.artefactLink);
      showSuccessNotification({
        contentComponent: `${artefactTypeLabel} “${name.trim()}” is saved successfully and its link is copied to the clipboard.`,
        props: { isClosable: true },
      });
    }
    if (onProceedToSubscribe) {
      showSuccessNotification({
        contentComponent: `${artefactTypeLabel} “${name.trim()}” is saved successfully and you can subscribe to feedback.`,
        props: { isClosable: true },
      });
      onProceedToSubscribe();
    }
    handleCloseModal();
  };

  const getModalTitle = () => {
    if (onCopyLink) {
      return `Save ${artefactTypeLabel} to Copy Link`;
    }
    if (onProceedToSubscribe) {
      return `Save ${artefactTypeLabel} to Subscribe to Feedback`;
    }
    return `Save ${artefactTypeLabel}`;
  };

  const getSaveButtonLabel = () => {
    if (onCopyLink) {
      return 'Save and Copy Link';
    }
    if (onProceedToSubscribe) {
      return 'Save and Proceed to Subscribe';
    }
    return 'Save';
  };

  const renderSaveButton = () => {
    return (
      <LoadingButton
        variant="contained"
        type="submit"
        disabled={isSaveDisabled}
        loading={isLoading}
        endIcon={<ReturnHotKeyIcon isDisabled={!name.trim()} />}
      >
        {buttonText || getSaveButtonLabel()}
      </LoadingButton>
    );
  };

  return (
    <>
      <ModalHeader
        title={headerText || getModalTitle()}
        showCloseButton={false}
      />
      <Box component="form" onSubmit={onArtefactSave}>
        <DialogContent dividers>
          <Stack py={2} gap={4}>
            {variant === 'create' && (
              <TextField
                autoFocus
                defaultValue={defaultValue}
                label={`${artefactTypeLabel} Name`}
                type="text"
                placeholder="Enter descriptive name"
                name={`${artefactType}-name`}
                value={name}
                onChange={onNameChange}
              />
            )}
            {!!secondaryActionNode && secondaryActionNode}
            {shouldShowArtefactDefinitionInfo &&
              artefactType === ArtefactType.Feed && (
                <Box
                  sx={{
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    maxHeight: '40vh',
                    overflowY: 'auto',
                  }}
                >
                  <Box p={2} color="text.secondary">
                    <FeedDefinitionInfo
                      shouldIncludeDashboardFilters={
                        shouldIncludeDashboardFilters
                      }
                      shouldShowDashboardFiltersSeparately={false}
                    />
                  </Box>
                </Box>
              )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" m={2} spacing={3}>
            <Button
              variant="outlined"
              onClick={handleCloseModal}
              type="button"
              disabled={isLoading}
              endIcon={<EscHotKeyIcon isDisabled={isLoading} />}
            >
              Cancel
            </Button>
            {isSaveDisabled ? (
              <Tooltip
                title="Please add a name to save the Feed"
                placement="top"
                arrow
              >
                <Box>{renderSaveButton()}</Box>
              </Tooltip>
            ) : (
              renderSaveButton()
            )}
          </Stack>
        </DialogActions>
      </Box>
    </>
  );
};
